import { useEffect, useRef, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { supabase } from '@/integrations/supabase/client'

export type SubscriptionStatus = 'trial' | 'trialing' | 'active' | 'cancelled' | 'expired' | 'loading' | 'none'

export function useSubscription() {
  const { user, isLoaded } = useUser()
  const [status, setStatus] = useState<SubscriptionStatus>('loading')
  const [daysLeft, setDaysLeft] = useState<number | null>(null)
  const [trialEndsAt, setTrialEndsAt] = useState<Date | null>(null)

  // Same loop guard as Onboarding: Clerk's `user` ref churns every render,
  // so naïve `[user, isLoaded]` deps re-fire the query every render. That
  // setStatus chain re-renders ProtectedRoute, which can unmount/remount the
  // child route, restarting Onboarding's hydration spinner. Lock the query
  // to ONE run per externalId.
  const queriedForUidRef = useRef<string | null>(null)
  const supabaseUuid = user?.externalId ?? null

  useEffect(() => {
    if (!isLoaded) return
    if (!user) { setStatus('loading'); return }
    if (!supabaseUuid) { setStatus('loading'); return }
    if (queriedForUidRef.current === supabaseUuid) return
    queriedForUidRef.current = supabaseUuid

    supabase
      .from('subscriptions')
      .select('status, trial_ends_at, current_period_end')
      .eq('user_id', supabaseUuid)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) { setStatus('none'); return }
        if (data.status === 'trial' || data.status === 'trialing') {
          const ends = new Date(data.trial_ends_at)
          const now = new Date()
          const days = Math.ceil((ends.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
          setTrialEndsAt(ends)
          setDaysLeft(Math.max(0, days))
          setStatus(days <= 0 ? 'expired' : 'trial')
        } else {
          setStatus(data.status as SubscriptionStatus)
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, supabaseUuid])

  return { status, daysLeft, trialEndsAt }
}
